import { getQueryClientInstance } from '@/api/queries/queryClient';
import { useAuthContext } from '@/context/AuthContext';
import useCountryOverrideStore from '@/dev/CountryOverrideDevTools/useCountryOverrideStore';
import { isAuthError, isOpaqueRequestError } from '@/utils/auth/isAuthError';
import { useEffect } from 'react';

/**
 * Handles an expired/invalid token surfaced by a query: makes a single controlled refresh
 * attempt (deduplicated across all callers). On success the queries are invalidated so they
 * refetch with the fresh token; on failure the refresh flow redirects to re-authentication.
 *
 * Also triggers on opaque failures (CORS-masked API-Gateway rejections with no readable
 * status) — these only reach the error state after the retry cap is exhausted, so a transient
 * blip does not escalate.
 */
const useInvalidTokenHandler = (error: unknown): void => {
  const { ensureValidSession } = useAuthContext();

  useEffect(() => {
    if (!isAuthError(error) && !isOpaqueRequestError(error)) {
      return;
    }

    // A stale country override (one that outlived a token change) makes the backend reject every
    // query as Unauthorized — a refresh cannot fix that and would loop into re-auth. So if an
    // override is active, clear it first (which drops the cache and refetches as full access); a
    // genuinely expired token then falls through on the next failure, which carries no override.
    const { countries, clear } = useCountryOverrideStore.getState();
    if (countries.length) {
      clear();
      return;
    }

    void ensureValidSession().then(recovered => {
      if (recovered) {
        void getQueryClientInstance().invalidateQueries();
      }
    });
  }, [error, ensureValidSession]);
};

export default useInvalidTokenHandler;
